import { isInternalRole } from "../../worker/internal-roles";
import { createFileRoute, Link, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, Copy, Radar } from "lucide-react";
import { Button, buttonClasses } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Container } from "@/components/container";

const searchSchema = z.object({
  sid: z.string().optional().default(""),
});

export const Route = createFileRoute("/jobs/$slug/submitted")({
  validateSearch: searchSchema,
  component: SubmittedPage,
});

function SubmittedPage() {
  const { slug } = useParams({ from: "/jobs/$slug/submitted" });
  const { sid } = useSearch({ from: "/jobs/$slug/submitted" });
  const statusPath = `/jobs/${encodeURIComponent(slug)}/status?sid=${encodeURIComponent(sid)}`;
  const statusLink = typeof window === "undefined" ? statusPath : new URL(statusPath, window.location.origin).toString();
  const [copied, setCopied] = useState(false);

  async function copyStatusLink() {
    await navigator.clipboard.writeText(statusLink);
    setCopied(true);
  }

  return (
    <Container width="lg" className="py-12 sm:py-16">
      <Card className="bb-dark-panel overflow-hidden rounded-lg">
        <CardContent className="p-7 sm:p-12">
          <div className="mx-auto max-w-3xl text-left">
            {sid ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                  <Radar className="size-5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl font-semibold">Application received</h1>
                  <p className="mt-2 text-sm text-muted-foreground">Your resume is saved and your application is being processed. You do not need to apply again.</p>
                  <p className="mt-4 text-sm font-bold uppercase tracking-[0.24em] text-muted-foreground">Submission id</p>
                  <p className="mt-1 break-all font-mono text-lg font-bold">{sid}</p>
                  <p className="mt-4 text-sm leading-6 text-muted-foreground">
                    Copy this private status link and keep it somewhere safe. Use it to check for updates.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <Input className="font-mono" readOnly value={statusLink} onFocus={(ev) => ev.currentTarget.select()} />
                    <Button type="button" onClick={copyStatusLink} className="shrink-0">
                      <Copy aria-hidden="true" />
                      {copied ? "Copied" : "Copy link"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <CheckCircle2 className="mt-1 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div>
                  <p className="font-medium text-foreground">Submission received</p>
                  <p className="mt-1 text-sm text-muted-foreground">No submission id was provided in this URL.</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {sid && (
          <Link to="/jobs/$slug/status" params={{ slug }} search={{ sid }} className={buttonClasses()}>
            View application status
          </Link>
        )}
        <Link to={isInternalRole({ slug }) ? "/" : "/jobs/$slug"} params={{ slug }} className={buttonClasses({ variant: "outline" })}>{isInternalRole({ slug }) ? "Back to jobs" : "Back to role"}</Link>
        <Link to="/" className={buttonClasses({ variant: sid ? "outline" : "primary" })}>See other roles</Link>
      </div>
    </Container>
  );
}
